import { Injectable, signal, computed } from '@angular/core';

/**
 * Service that tracks in-flight HTTP requests using a request counter.
 * Exposes signal-based state for reactive UI updates.
 * Used by the loading interceptor to increment/decrement the counter.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  /** Internal request counter */
  private readonly requestCount = signal(0);

  /** Whether any HTTP request is in progress */
  readonly isLoading = computed(() => this.requestCount() > 0);

  /**
   * Increment the request counter. Called by the loading interceptor
   * when a new HTTP request starts.
   */
  startRequest(): void {
    this.requestCount.update(count => count + 1);
  }

  /**
   * Decrement the request counter. Called by the loading interceptor
   * when an HTTP request completes (success or error).
   */
  endRequest(): void {
    this.requestCount.update(count => Math.max(0, count - 1));
  }

  /**
   * Force reset the loading state. Useful for cleanup after errors
   * that might leave the counter in an inconsistent state.
   */
  forceReset(): void {
    this.requestCount.set(0);
  }
}
